import { useState } from 'react';
import { t } from '@/i18n';
import type { ChartVisualType, ChartSeriesConfig, InteractionMode, ChartInteractionConfig } from '@/types/chart';
import {
  isOverrideAllowed,
  MIXED_TYPES,
  DEFAULT_INTERACTION_MODE,
} from '@/types/chart';
import { AccordionField } from '@/components/common/AccordionField';
import { ColorPicker } from '@/components/common/ColorPicker';
import { ValueFormattingSection } from '@/components/common/ValueFormattingSection';

const CHART_TYPE_OPTIONS: { value: ChartVisualType; labelKey: string }[] = [
  { value: 'line', labelKey: 'dashboard.widget.chart.type.line' },
  { value: 'area', labelKey: 'dashboard.widget.chart.type.area' },
  { value: 'bar', labelKey: 'dashboard.widget.chart.type.bar' },
  { value: 'hbar', labelKey: 'dashboard.widget.chart.type.hbar' },
  { value: 'pie', labelKey: 'dashboard.widget.chart.type.pie' },
  { value: 'doughnut', labelKey: 'dashboard.widget.chart.type.doughnut' },
  { value: 'radar', labelKey: 'dashboard.widget.chart.type.radar' },
  { value: 'funnel', labelKey: 'dashboard.widget.chart.type.funnel' },
  { value: 'radialGauge', labelKey: 'dashboard.widget.chart.type.radialGauge' },
];

const CARTESIAN_TYPES: ChartVisualType[] = ['line', 'area', 'bar', 'hbar'];

const DEFAULT_COLORS = [
  '#3a57e8', '#1aa053', '#c03221', '#f16a1b', '#079aa2',
  '#6c757d', '#e83e8c', '#6610f2', '#fd7e14', '#20c997',
];

function getTypeLabelKey(type: ChartVisualType): string {
  const opt = CHART_TYPE_OPTIONS.find((o) => o.value === type);
  return opt?.labelKey ?? 'dashboard.widget.chart.type.line';
}

function getOverrideHint(override: ChartVisualType | undefined): string | null {
  if (!override) return null;
  if (override === 'hbar') return t('dashboard.widget.chart.hint.hbar');
  if (override === 'radar') return t('dashboard.widget.chart.hint.radar');
  if (['line', 'area', 'bar'].includes(override)) return t('dashboard.widget.chart.hint.cartesian');
  return null;
}

function isCartesian(type: ChartVisualType): boolean {
  return CARTESIAN_TYPES.includes(type);
}

interface ChartSettingsProps {
  config: Record<string, unknown>;
  onConfigChange: (config: Record<string, unknown>) => void;
}

export function ChartSettings({ config, onConfigChange }: ChartSettingsProps) {
  const chartType = (config.chartType as ChartVisualType) ?? 'line';
  const series = (config.series as ChartSeriesConfig[]) ?? [];
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());
  const [newSeriesLabel, setNewSeriesLabel] = useState('');

  const handleChange = (field: string, value: unknown) => {
    onConfigChange({ ...config, chartType, [field]: value });
  };

  const updateSeries = (newSeries: ChartSeriesConfig[]) => {
    onConfigChange({ ...config, chartType, series: newSeries });
  };

  const handleChartTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value as ChartVisualType;
    if (!isOverrideAllowed(newType)) {
      // Non-mixed types: очистить chartTypeOverride у всех серий,
      // иначе старый override продолжит переопределять тип серии
      const cleanedSeries = series.map((s) => ({ ...s, chartTypeOverride: undefined }));
      onConfigChange({ ...config, chartType: newType, series: cleanedSeries });
    } else {
      handleChange('chartType', newType);
    }
  };

  const handleAddSeries = () => {
    if (series.length >= 10) return;

    const fieldName = `series_${Date.now()}`;
    const newSeries: ChartSeriesConfig = {
      fieldName,
      label: newSeriesLabel,
      color: DEFAULT_COLORS[series.length % DEFAULT_COLORS.length],
    };
    updateSeries([...series, newSeries]);
    setNewSeriesLabel('');
  };

  const handleRemoveSeries = (fieldName: string) => {
    updateSeries(series.filter((s) => s.fieldName !== fieldName));
    setExpandedFields((prev) => {
      const next = new Set(prev);
      next.delete(fieldName);
      return next;
    });
  };

  const handleSeriesChange = (fieldName: string, key: string, value: unknown) => {
    updateSeries(
      series.map((s) =>
        s.fieldName === fieldName ? { ...s, [key]: value } : s,
      ),
    );
  };

  const handleMoveUp = (fieldName: string) => {
    const idx = series.findIndex((s) => s.fieldName === fieldName);
    if (idx <= 0) return;
    const newSeries = [...series];
    [newSeries[idx - 1], newSeries[idx]] = [newSeries[idx], newSeries[idx - 1]];
    updateSeries(newSeries);
  };

  const handleMoveDown = (fieldName: string) => {
    const idx = series.findIndex((s) => s.fieldName === fieldName);
    if (idx >= series.length - 1) return;
    const newSeries = [...series];
    [newSeries[idx], newSeries[idx + 1]] = [newSeries[idx + 1], newSeries[idx]];
    updateSeries(newSeries);
  };

  const handleToggle = (fieldName: string) => {
    setExpandedFields((prev) => {
      const next = new Set(prev);
      if (next.has(fieldName)) {
        next.delete(fieldName);
      } else {
        next.add(fieldName);
      }
      return next;
    });
  };

  const cartesian = isCartesian(chartType);

  return (
    <div>
      <h5>{t('dashboard.widget.chart.section.mainSettings')}</h5>

      <div className="mb-3">
        <label className="form-label">{t('dashboard.widget.chart.field.chartType')}</label>
        <select
          className="form-select"
          data-testid="chart-settings-type"
          value={chartType}
          onChange={handleChartTypeChange}
        >
          <option value="line">{t('dashboard.widget.chart.type.line')} / {t('dashboard.widget.chart.type.area')} / {t('dashboard.widget.chart.type.bar')}</option>
          <option value="hbar">{t('dashboard.widget.chart.type.hbar')}</option>
          <option value="radar">{t('dashboard.widget.chart.type.radar')}</option>
          <option value="funnel">{t('dashboard.widget.chart.type.funnel')}</option>
          <option value="pie">{t('dashboard.widget.chart.type.pie')}</option>
          <option value="doughnut">{t('dashboard.widget.chart.type.doughnut')}</option>
          <option value="radialGauge">{t('dashboard.widget.chart.type.radialGauge')}</option>
        </select>
      </div>

      <div className="mb-3">
        <label className="form-label">{t('dashboard.widget.chart.field.description')}</label>
        <textarea
          className="form-control"
          data-testid="chart-settings-description"
          placeholder={t('dashboard.widget.chart.field.description')}
          value={(config.description as string) ?? ''}
          onChange={(e) => handleChange('description', e.target.value)}
        />
      </div>

      <div className="mb-3">
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            data-testid="chart-settings-show-legend"
            id="chart-settings-show-legend"
            checked={!!config.showLegend}
            onChange={(e) => handleChange('showLegend', e.target.checked)}
          />
          <label className="form-check-label" htmlFor="chart-settings-show-legend">
            {t('dashboard.widget.chart.field.showLegend')}
          </label>
        </div>
      </div>

      {config.showLegend === true && (
        <div className="mb-3">
          <label className="form-label">{t('dashboard.widget.chart.field.legendPosition')}</label>
          <select
            className="form-select"
            data-testid="chart-settings-legend-position"
            value={(config.legendPosition as string) || 'top'}
            onChange={(e) => handleChange('legendPosition', e.target.value)}
          >
            {(['top', 'bottom', 'left', 'right'] as const).map((pos) => (
              <option key={pos} value={pos}>
                {t(`dashboard.widget.chart.legendPosition.${pos}`)}
              </option>
            ))}
          </select>
        </div>
      )}

      {chartType === 'funnel' && (
        <div className="mb-3">
          <label className="form-label">{t('dashboard.widget.chart.field.funnelOrientation')}</label>
          <select
            className="form-select"
            data-testid="chart-settings-funnelOrientation"
            value={(config.funnelOrientation as string) ?? 'vertical'}
            onChange={(e) => handleChange('funnelOrientation', e.target.value || undefined)}
          >
            <option value="vertical">{t('dashboard.widget.chart.funnelOrientation.vertical')}</option>
            <option value="horizontal">{t('dashboard.widget.chart.funnelOrientation.horizontal')}</option>
          </select>
        </div>
      )}

      {chartType === 'funnel' && (
        <div className="mb-3">
          <label className="form-label">Размер шрифта подписи</label>
          <input
            type="number"
            className="form-control"
            data-testid="chart-settings-funnelDataLabelFontSize"
            value={(config.funnelDataLabelFontSize as number) ?? ''}
            onChange={(e) => handleChange('funnelDataLabelFontSize', e.target.value ? Number(e.target.value) : undefined)}
            min={8}
            max={48}
            placeholder="12"
          />
        </div>
      )}

      {chartType === 'funnel' && (
        <div className="mb-3">
          <label className="form-label">Цвет подписи</label>
          <ColorPicker
            value={(config.funnelDataLabelColor as string) ?? '#374151'}
            onChange={(color) => handleChange('funnelDataLabelColor', color)}
          />
        </div>
      )}

      {chartType === 'funnel' && (
        <div className="mb-3">
          <label className="form-label">Масштаб пропорций</label>
          <select
            className="form-select"
            data-testid="chart-settings-funnelScale"
            value={(config.funnelScale as string) ?? 'linear'}
            onChange={(e) => handleChange('funnelScale', e.target.value || undefined)}
          >
            <option value="linear">Линейный</option>
            <option value="sqrt">Сглаженный (sqrt)</option>
            <option value="cbrt">Плавный (cbrt)</option>
            <option value="log">Логарифмический (log10)</option>
          </select>
          <div className="form-text">Сглаживание уменьшает визуальную разницу между большими и малыми значениями</div>
        </div>
      )}

      <div className="mb-3">
        <label className="form-label">{t('dashboard.widget.chart.interaction.mode')}</label>
        <select
          className="form-select"
          data-testid="chart-settings-interaction-mode"
          value={(config.interactions as ChartInteractionConfig | undefined)?.mode ?? DEFAULT_INTERACTION_MODE}
          onChange={(e) => {
            const mode = e.target.value as InteractionMode;
            const existing = (config.interactions as ChartInteractionConfig | undefined) ?? {};
            handleChange('interactions', { ...existing, mode });
          }}
        >
          <option value="point">{t('dashboard.widget.chart.mode.point')}</option>
          <option value="nearest">{t('dashboard.widget.chart.mode.nearest')}</option>
          <option value="index">{t('dashboard.widget.chart.mode.index')}</option>
        </select>
      </div>

      <div className="mb-3 form-check">
        <input
          type="checkbox"
          className="form-check-input"
          data-testid="chart-settings-interaction-intersect"
          id="chart-settings-interaction-intersect"
          checked={(config.interactions as ChartInteractionConfig | undefined)?.intersect ?? false}
          onChange={(e) => {
            const existing = (config.interactions as ChartInteractionConfig | undefined) ?? {};
            handleChange('interactions', { ...existing, intersect: e.target.checked });
          }}
        />
        <label className="form-check-label" htmlFor="chart-settings-interaction-intersect">
          {t('dashboard.widget.chart.interaction.intersect')}
        </label>
      </div>

      {cartesian && (
        <div className="mb-3">
          <label className="form-label">{t('dashboard.widget.chart.field.xAxisLabel')}</label>
          <input
            type="text"
            className="form-control"
            data-testid="chart-settings-x-axis-label"
            value={(config.xAxisLabel as string) ?? ''}
            onChange={(e) => handleChange('xAxisLabel', e.target.value)}
          />
        </div>
      )}

      {cartesian && (
        <div className="mb-3">
          <label className="form-label">{t('dashboard.widget.chart.field.yAxisLabel')}</label>
          <input
            type="text"
            className="form-control"
            data-testid="chart-settings-y-axis-label"
            value={(config.yAxisLabel as string) ?? ''}
            onChange={(e) => handleChange('yAxisLabel', e.target.value)}
          />
        </div>
      )}

      {cartesian && (
        <div className="mb-3 form-check">
          <input
            type="checkbox"
            className="form-check-input"
            data-testid="chart-settings-show-axes"
            id="chart-settings-show-axes"
            checked={(config.showAxes as boolean) ?? true}
            onChange={(e) => handleChange('showAxes', e.target.checked)}
          />
          <label className="form-check-label" htmlFor="chart-settings-show-axes">
            {t('dashboard.widget.chart.field.showAxes')}
          </label>
        </div>
      )}

      <h5>{t('dashboard.widget.chart.section.series')}</h5>

      <div className="accordion">
        {series.map((s, idx) => {
          const testIdPrefix = `chart-series-${s.fieldName}`;
          const isExpanded = expandedFields.has(s.fieldName);

          const effectiveType = s.chartTypeOverride ?? chartType;
          const overrideHint = getOverrideHint(s.chartTypeOverride);

          return (
            <AccordionField
              key={s.fieldName}
              fieldName={s.fieldName}
              label={s.label || s.fieldName}
              summary={t(getTypeLabelKey(effectiveType))}
              isExpanded={isExpanded}
              onToggle={handleToggle}
              testIdPrefix={testIdPrefix}
            >
              <div className="mb-3">
                <label className="form-label">{t('dashboard.widget.chart.field.label')}</label>
                <input
                  type="text"
                  className="form-control"
                  data-testid={`${testIdPrefix}-label`}
                  value={s.label}
                  onChange={(e) => handleSeriesChange(s.fieldName, 'label', e.target.value)}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">{t('dashboard.widget.chart.field.color')}</label>
                <ColorPicker
                  value={s.color ?? DEFAULT_COLORS[idx % DEFAULT_COLORS.length]}
                  onChange={(color) => handleSeriesChange(s.fieldName, 'color', color)}
                />
              </div>

              {isOverrideAllowed(chartType) && (
                <div className="mb-3">
                  <label className="form-label">{t('dashboard.widget.chart.field.chartTypeOverride')}</label>
                  <select
                    className="form-select"
                    data-testid={`${testIdPrefix}-chartTypeOverride`}
                    value={s.chartTypeOverride ?? ''}
                    onChange={(e) =>
                      handleSeriesChange(
                        s.fieldName,
                        'chartTypeOverride',
                        e.target.value || undefined,
                      )
                    }
                  >
                    {MIXED_TYPES.map((type) => {
                      const opt = CHART_TYPE_OPTIONS.find((o) => o.value === type);
                      return opt ? (
                        <option key={opt.value} value={opt.value}>
                          {t(opt.labelKey)}
                        </option>
                      ) : null;
                    })}
                  </select>
                  {overrideHint && (
                    <div className="form-text">{overrideHint}</div>
                  )}
                </div>
              )}

              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`${testIdPrefix}-showDataLabels`}
                  data-testid={`${testIdPrefix}-showDataLabels`}
                  checked={s.showDataLabels ?? false}
                  onChange={(e) => handleSeriesChange(s.fieldName, 'showDataLabels', e.target.checked)}
                />
                <label className="form-check-label" htmlFor={`${testIdPrefix}-showDataLabels`}>
                  {t('dashboard.widget.chart.showDataLabels')}
                </label>
              </div>

              {effectiveType === 'line' || effectiveType === 'area' ? (
                <>
                  <div className="mb-3 form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`${testIdPrefix}-showPoints`}
                      data-testid={`${testIdPrefix}-showPoints`}
                      checked={s.showPoints !== false}
                      onChange={(e) => handleSeriesChange(s.fieldName, 'showPoints', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor={`${testIdPrefix}-showPoints`}>
                      {t('dashboard.widget.chart.field.showPoints')}
                    </label>
                  </div>
                  {s.showPoints !== false && (
                    <div className="mb-3">
                      <label className="form-label">{t('dashboard.widget.chart.field.pointRadius')}</label>
                      <input
                        type="number"
                        className="form-control"
                        data-testid={`${testIdPrefix}-pointRadius`}
                        value={s.pointRadius ?? 3}
                        onChange={(e) => handleSeriesChange(s.fieldName, 'pointRadius', e.target.value === '' ? undefined : Number(e.target.value))}
                        min={0}
                        max={20}
                        step={1}
                      />
                    </div>
                  )}
                </>
              ) : null}

              <ValueFormattingSection
                prefix={s.prefix}
                suffix={s.suffix}
                useMillions={s.useMillions}
                millionsFrom={s.millionsFrom}
                decimalPlaces={s.decimalPlaces}
                onChange={(key, value) => handleSeriesChange(s.fieldName, key, value)}
                testIdPrefix={`${testIdPrefix}-fmt`}
              />

              <div className="d-flex gap-1 mt-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  data-testid={`${testIdPrefix}-move-up`}
                  disabled={idx === 0}
                  onClick={() => handleMoveUp(s.fieldName)}
                >
                  <i className="las la-arrow-up" /> {t('dashboard.widget.chart.moveUp')}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  data-testid={`${testIdPrefix}-move-down`}
                  disabled={idx === series.length - 1}
                  onClick={() => handleMoveDown(s.fieldName)}
                >
                  <i className="las la-arrow-down" /> {t('dashboard.widget.chart.moveDown')}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm ms-auto"
                  data-testid={`${testIdPrefix}-remove`}
                  onClick={() => handleRemoveSeries(s.fieldName)}
                >
                  <i className="las la-trash" /> {t('dashboard.delete')}
                </button>
              </div>
            </AccordionField>
          );
        })}
      </div>

      {chartType !== 'radialGauge' && (
        <div className="input-group mt-2">
          <input
            type="text"
            className="form-control"
            placeholder={t('dashboard.widget.chart.field.label')}
            data-testid="chart-settings-new-series-label"
            value={newSeriesLabel}
            onChange={(e) => setNewSeriesLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddSeries(); }}
          />
          <button
            type="button"
            className="btn btn-outline-primary"
            data-testid="chart-settings-add-series"
            onClick={handleAddSeries}
          >
            <i className="las la-plus" /> {t('dashboard.widget.chart.addSeries')}
          </button>
        </div>
      )}
    </div>
  );
}
